import React from 'react';
import { computeProgressMetrics } from '../../utils/progressBar';
import './ProgressBar.css';

export type ProgressBarVariant = 'running' | 'completed' | 'failed' | 'warning';

export interface ProgressBarProps {
  completed?: number;
  total?: number;
  failed?: number;
  /** Direct percent 0–100; when set, overrides completed/total math */
  percent?: number;
  subLabel?: string;
  variant?: ProgressBarVariant;
  className?: string;
  inline?: boolean;
  showFraction?: boolean;
  title?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  completed = 0,
  total = 0,
  failed = 0,
  percent,
  subLabel,
  variant = 'running',
  className = '',
  inline = false,
  showFraction = true,
  title,
}) => {
  const metrics =
    typeof percent === 'number'
      ? {
          ...computeProgressMetrics(percent, 100, 0),
          percent: Math.min(100, Math.max(0, percent)),
          failedPercent: 0,
        }
      : computeProgressMetrics(completed, total, failed);

  if (metrics.total <= 0 && typeof percent !== 'number') {
    return null;
  }

  const fillClass =
    variant === 'completed'
      ? 'progress-bar__fill--done'
      : variant === 'failed'
        ? 'progress-bar__fill--failed'
        : variant === 'warning'
          ? 'progress-bar__fill--warning'
          : 'progress-bar__fill--completed';

  const failedTitle =
    metrics.failed > 0 ? `${metrics.failed} failed` : undefined;
  const tooltip = title ?? failedTitle;

  return (
    <div
      className={['progress-bar', inline ? 'progress-bar--inline' : '', className].filter(Boolean).join(' ')}
      title={tooltip}
    >
      {(showFraction || subLabel) && (
        <div className="progress-bar__meta">
          {showFraction && metrics.total > 0 && (
            <span className="progress-bar__fraction">
              {metrics.completed}/{metrics.total}
            </span>
          )}
          {typeof percent === 'number' && showFraction && metrics.total <= 0 && (
            <span className="progress-bar__fraction">{metrics.percent.toFixed(0)}%</span>
          )}
          {subLabel ? <span className="progress-bar__sub-label">{subLabel}</span> : null}
        </div>
      )}
      <div
        className="progress-bar__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={metrics.total > 0 ? metrics.total : 100}
        aria-valuenow={metrics.completed}
      >
        <div
          className={`progress-bar__fill ${fillClass}`}
          style={{ width: `${metrics.percent}%` }}
        />
        {metrics.failedPercent > 0 && (
          <div
            className="progress-bar__fill progress-bar__fill--failed"
            style={{ width: `${metrics.failedPercent}%` }}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
